/**
 * InventorySlots — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.inventorySlots,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';
import { changed } from './dirty.js';
import { WeaponFireMode } from '../../../../shared/Weapon.js';
import { slotAmmoView } from './ammoDisplay.js';

/** Weapon / ability HUD squares share this empty-vs-filled contrast. */
const EMPTY_SLOT_FILL_ALPHA = 0.35;
const FILLED_SLOT_FILL_ALPHA = 1;

/**
 * Update inventory slots
 * @param {Object} gameState - Game state
 * @param {Object} callbacks - Callback functions
 */
export function update(hud, gameState, callbacks) {
  const inv = hud.elements.inventorySlots;
  if (!inv) return;

  const localPlayer = gameState.players.get(gameState.localId);
  const { getWeaponIcon } = callbacks;
  // The mode, not the slot, says whether guns run dry: each client slot's
  // weaponState comes from createState(), whose unlimitedAmmo is always false.
  const unlimitedAmmo = Boolean(gameState.gameModeConfig?.unlimitedAmmo);
  const slotSize = 90; // Match ability slot size (20% larger than previous 75px)

  inv.slots.forEach((slot, index) => {
    // Check if hud inventory slot has a weapon for stack-based coloring
    const slotData = localPlayer?.inventory?.[index];
    const hasWeapon = slotData && slotData.item;
    const stackCount = slotData?.stack || 1;
    // Each slot carries its own weaponState (the active slot's is the live one)
    const weaponState = slotData?.weaponState;
    const ammoView = slotAmmoView(slotData?.item, weaponState, localPlayer?.ammo, unlimitedAmmo);

    // Everything hud slot draws depends on these values; redraw only when they change.
    // Loaded and reserve are tracked separately: a reload moves rounds between
    // them without changing their sum.
    const viewModel = {
      itemId: slotData?.item?.id ?? null,
      icon: slotData?.item?.icon ?? null,
      hasIcon: Boolean(slotData?.item?.icon && getWeaponIcon && getWeaponIcon(slotData.item.icon)),
      stack: stackCount,
      active: localPlayer?.activeSlotIndex === index,
      current: weaponState ? Math.round(weaponState.currentAmmo || 0) : null,
      reserve: ammoView ? ammoView.reserve : null,
      capacity: slotData?.item?.magCapacity ?? null
    };
    if (!changed(slot, 'slot', viewModel)) return;

    // Update background with gradient (only if equipped, otherwise white)
    slot.background.clear();
    
    if (hasWeapon) {
      // Stack-based gradient backgrounds: color->white
      const stackColors = {
        1: 0x22c55e,  // Green
        2: 0x3b82f6,  // Blue
        3: 0xa855f7,  // Purple
        4: 0xeab308   // Gold
      };
      const gradientColor = stackColors[Math.min(stackCount, 4)];
      
      // Use cached gradient texture to prevent WebGL context loss
      const cacheKey = `gradient_${gradientColor}_${slotSize}`;
      let texture = hud.gradientTextureCache[cacheKey];
      
      if (!texture) {
        // Create gradient texture only once and cache it
        const canvas = document.createElement('canvas');
        canvas.width = slotSize;
        canvas.height = slotSize;
        const ctx = canvas.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, 0, slotSize);
        
        // Convert hex to RGB for gradient
        const r = (gradientColor >> 16) & 0xFF;
        const g = (gradientColor >> 8) & 0xFF;
        const b = gradientColor & 0xFF;
        
        gradient.addColorStop(0, `rgb(${r}, ${g}, ${b})`);
        gradient.addColorStop(1, 'rgb(255, 255, 255)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, slotSize, slotSize);
        
        texture = PIXI.Texture.from(canvas);
        hud.gradientTextureCache[cacheKey] = texture;
      }
      
      slot.background.rect(0, 0, slotSize, slotSize);
      slot.background.alpha = FILLED_SLOT_FILL_ALPHA;
      slot.background.fill({ texture });
    } else {
      slot.background.rect(0, 0, slotSize, slotSize);
      slot.background.alpha = EMPTY_SLOT_FILL_ALPHA;
      slot.background.fill({ color: 0xffffff });
    }
    
    // Update border color based on active state
    const isActive = localPlayer?.activeSlotIndex === index;
    slot.border.clear();
    slot.border.setStrokeStyle({ width: 2, color: isActive ? 0x555555 : 0x000000 });
    slot.border.rect(0, 0, slotSize, slotSize);
    slot.border.stroke();

    // Highlight active slot
    slot.activeBorder.clear();
    if (isActive) {
      slot.activeBorder.setStrokeStyle({ width: 4, color: 0x00ffff }); // Cyan highlight
      slot.activeBorder.rect(-2, -2, slotSize + 4, slotSize + 4);
      slot.activeBorder.stroke();
    }

    // Get weapon from slot data
    const weapon = slotData?.item;

    if (weapon && weapon.icon) {
      const iconTexture = getWeaponIcon ? getWeaponIcon(weapon.icon) : null;
      if (iconTexture) {
        slot.icon.texture = iconTexture;
        slot.icon.visible = true;
        
        // Calculate proportional scaling to fit within slot
        const padding = 10;
        const maxSize = slotSize - padding;
        const textureWidth = iconTexture.width;
        const textureHeight = iconTexture.height;
        const scale = Math.min(maxSize / textureWidth, maxSize / textureHeight);
        slot.icon.width = textureWidth * scale;
        slot.icon.height = textureHeight * scale;
      } else {
        slot.icon.visible = false;
      }

      // Show stack indicator if stacked (the pickaxe never stacks — maxStacks: 1)
      if (slotData.stack > 1 && weapon.fireMode !== WeaponFireMode.MELEE) {
        slot.stackText.text = `x${slotData.stack}`;
        slot.stackText.x = slotSize - 4;
        slot.stackText.y = 4;
        slot.stackText.visible = true;
      } else {
        slot.stackText.visible = false;
      }

      // Ammo readout on the weapon tile. slotAmmoView returns null for the pickaxe
      // (excluded by fireMode), non-weapons and slots without a weaponState.
      if (ammoView) {
        slot.magText.text = ammoView.mag;
        slot.reserveText.text = ammoView.reserve;
        slot.magText.visible = true;
        slot.reserveText.visible = true;
      } else {
        slot.magText.visible = false;
        slot.reserveText.visible = false;
      }
    } else {
      slot.icon.visible = false;
      slot.stackText.visible = false;
      slot.magText.visible = false;
      slot.reserveText.visible = false;
    }
  });
}
